import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useNavigate } from "react-router"
import {
  Camera01,
  ChevronDown,
  ChevronRight,
  Edit01,
  RefreshCw01,
} from "@untitledui/icons"
import { vinApi } from "@/lib/api/vin"
import type { ApiEstimate } from "@/lib/api/types"
import { useVinDuplicates } from "@/lib/estimates/use-vin-duplicates"
import { decodeVIN } from "@/lib/estimates/vin-decoder"
import { validateVinFormat } from "@/lib/vin/vin-validator"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"
import { VIN_COPY } from "./vehicle-copy"
import type { VinDecodedVehicle } from "./vehicle-types"
import { VinDuplicateDialog } from "./vin-duplicate-dialog"

// Vehicle step (shared by QE and Full estimate wizards) — Step 1.
// Single-column, focused. One primary action: scan the VIN. A quiet
// secondary link drops the user into manual typing if needed. The
// "where do I find the VIN?" diagram is shown inline below the
// primary actions so it's always available without a hunt.

interface StepVehicleProps {
  vin: string
  vehicle: VinDecodedVehicle | null
  onVin: (vin: string) => void
  onVehicle: (vehicle: VinDecodedVehicle | null) => void
  onContinue: () => void
  /** Overrides the eyebrow label so this step can be reused inside
   *  wizards with different step counts (QE2 / QE3 / full-estimate). */
  eyebrowLabel?: string
}

type Mode =
  | { kind: "idle" }
  | { kind: "scanning"; previewUrl: string }
  | { kind: "decoding"; previewUrl: string; vin: string }
  | { kind: "scan-failed"; previewUrl: string }
  | { kind: "typing" }

export function StepVehicle({
  vin,
  vehicle,
  onVin,
  onVehicle,
  onContinue,
  eyebrowLabel = "Step 1 of 3 · Vehicle",
}: StepVehicleProps) {
  const [mode, setMode] = useState<Mode>({ kind: "idle" })
  const [scanFailures, setScanFailures] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // ── VIN duplicate check (req 2.5.1/2.5.2) ──
  // A well-formed VIN the advisor has entered — from a decoded scan, the
  // wizard's stored vin, or a typed candidate that hasn't decoded yet. The
  // check fires on the number itself: a VIN the decoder rejects can still
  // have estimates on file.
  const [candidateVin, setCandidateVin] = useState("")
  const resolvedVin = (vehicle?.vin || vin || candidateVin)
    .replace(/[^A-HJ-NPR-Z0-9]/gi, "")
    .toUpperCase()
  // Matches span the user's active dealership AND body shop (see the hook),
  // so the dealership → body-shop hand-off surfaces, not just the one team.
  const duplicates = useVinDuplicates(resolvedVin)
  const [dupMatches, setDupMatches] = useState<ApiEstimate[] | null>(null)
  // Fires once per VIN entry. Seeded with the mount-time VIN so a step
  // revisit (or a sessionStorage rehydrate) doesn't re-nag about a VIN
  // the advisor already acknowledged.
  const checkedVinRef = useRef<string | null>(
    resolvedVin.length === 17 ? resolvedVin : null,
  )

  useEffect(() => {
    if (duplicates.length === 0) return
    if (checkedVinRef.current === resolvedVin) return
    checkedVinRef.current = resolvedVin
    setDupMatches(duplicates)
  }, [duplicates, resolvedVin])

  const handleScanClick = () => {
    try {
      navigator.vibrate?.(10)
    } catch {
      // ignore
    }
    fileInputRef.current?.click()
  }

  const handleFile = async (file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setMode({ kind: "scanning", previewUrl })
    const fail = () => {
      setScanFailures((n) => n + 1)
      setMode({ kind: "scan-failed", previewUrl })
    }
    try {
      // Image → VIN runs server-side. 404 (endpoint not yet shipped),
      // 422 (no VIN found), and any other 4xx/5xx all throw ApiError;
      // we collapse them into one "couldn't read it" verdict — the
      // user retakes or switches to manual typing.
      const { vin: scannedVin } = await vinApi.fromImage(file)
      if (!validateVinFormat(scannedVin)) return fail()
      setMode({ kind: "decoding", previewUrl, vin: scannedVin })
      onVin(scannedVin)
      try {
        const decoded = await decodeVIN(scannedVin)
        // Hard-gate on BE recognition. Empty make/model means the BE
        // couldn't validate the VIN against any registry — treat the
        // same as "couldn't read it" so the user retakes or types in
        // a corrected one. Carrying empty fields downstream produced
        // ghost vehicles (ClickUp 869d7dcr8).
        if (!decoded.make || !decoded.model) {
          return fail()
        }
        onVehicle({
          vin: scannedVin,
          year: decoded.year,
          make: decoded.make,
          model: decoded.model,
          trim: decoded.trim,
          type: decoded.type,
        })
        setMode({ kind: "idle" })
      } catch {
        return fail()
      }
    } catch {
      fail()
    }
  }

  const handleSwitchToTyping = () => setMode({ kind: "typing" })

  const handleRescan = () => {
    onVehicle(null)
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {eyebrowLabel}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {vehicle
            ? "Got it"
            : mode.kind === "scan-failed"
              ? "Couldn't read it"
              : mode.kind === "typing"
                ? "Type the VIN"
                : "What's the VIN?"}
        </h2>
        {!vehicle && mode.kind === "idle" && (
          <p className="text-[15px] text-zinc-500 mt-2 leading-relaxed">
            Snap a photo of the VIN and we'll decode the rest.
          </p>
        )}
        {mode.kind === "typing" && (
          <p className="text-[15px] text-zinc-500 mt-2 leading-relaxed">
            17 characters from the windshield, door jamb, or registration.
          </p>
        )}
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
          e.target.value = ""
        }}
        className="hidden"
      />

      <AnimatePresence mode="wait">
        {/* Idle — primary scan, quiet type link, location hints
            collapsed under a "Can't find it?" disclosure. */}
        {mode.kind === "idle" && !vehicle && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-5"
          >
            <ScanButton onClick={handleScanClick} />
            <button
              type="button"
              onClick={handleSwitchToTyping}
              className="w-full text-center text-[13.5px] font-semibold text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors duration-150"
            >
              I'd rather type it in
            </button>
            <VinLocationsDisclosure />
          </motion.div>
        )}

        {/* Scanning + decoding */}
        {(mode.kind === "scanning" || mode.kind === "decoding") && (
          <motion.div
            key={mode.kind}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="space-y-4"
          >
            <CapturedThumb url={mode.previewUrl} />
            <DecodeSkeleton label={VIN_COPY.reading} />
          </motion.div>
        )}

        {/* Success — quiet card. This is one step of three; we
            don't celebrate it like flow-end. */}
        {mode.kind === "idle" && vehicle && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className="space-y-5"
          >
            <div className="rounded-2xl border border-zinc-200 bg-white p-5">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5">
                Decoded
              </p>
              <p className="text-[18px] font-semibold text-zinc-900 leading-tight">
                {[vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
                  .filter(Boolean)
                  .join(" ")}
              </p>
              <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                <code className="text-[12px] font-mono text-zinc-600 bg-zinc-50 px-2 py-0.5 rounded border border-zinc-200">
                  {vehicle.vin}
                </code>
                <button
                  type="button"
                  onClick={handleSwitchToTyping}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
                >
                  <Edit01 className="size-3" aria-hidden />
                  {VIN_COPY.editLabel}
                </button>
                <span className="text-zinc-300" aria-hidden>·</span>
                <button
                  type="button"
                  onClick={handleRescan}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
                >
                  <Camera01 className="size-3" aria-hidden />
                  {VIN_COPY.rescanLabel}
                </button>
              </div>
            </div>
            <PrimaryButton onClick={onContinue} type="button" size="wide">
              Continue to photos
              <ChevronRight className="size-4" aria-hidden />
            </PrimaryButton>
          </motion.div>
        )}

        {/* Scan failed */}
        {mode.kind === "scan-failed" && (
          <motion.div
            key="scan-failed"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="space-y-4"
          >
            <CapturedThumb url={mode.previewUrl} />
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
              <p className="text-[14px] font-semibold text-amber-900 leading-snug">
                {VIN_COPY.scanFailed}
              </p>
            </div>
            {scanFailures < 2 ? (
              <div className="flex flex-col gap-2">
                <PrimaryButton onClick={handleScanClick} type="button" size="wide">
                  <RefreshCw01 className="size-4" aria-hidden />
                  {VIN_COPY.retakeLabel}
                </PrimaryButton>
                <button
                  type="button"
                  onClick={handleSwitchToTyping}
                  className="w-full text-center text-[13px] font-semibold text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors duration-150 py-2"
                >
                  I'd rather type it in
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <PrimaryButton onClick={handleSwitchToTyping} type="button" size="wide">
                  Type it instead
                </PrimaryButton>
                <button
                  type="button"
                  onClick={handleScanClick}
                  className="w-full text-center text-[13px] font-semibold text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors duration-150 py-2 inline-flex items-center justify-center gap-1.5"
                >
                  <RefreshCw01 className="size-3.5" aria-hidden />
                  Or try the photo again
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Manual typing */}
        {mode.kind === "typing" && (
          <ManualVinInput
            initialVin={vin}
            onVinCandidate={setCandidateVin}
            onCancel={() => setMode({ kind: "idle" })}
            onResolved={(decoded) => {
              // Set both state.vin and state.vehicle so downstream
              // consumers reading either field find the typed VIN.
              onVin(decoded.vin)
              onVehicle(decoded)
              setMode({ kind: "idle" })
            }}
          />
        )}
      </AnimatePresence>

      {/* Dismissed the popup but changed your mind? The fact doesn't vanish —
          a quiet line brings the earlier estimates back (#70). */}
      {duplicates.length > 0 && dupMatches === null && (
        <button
          type="button"
          onClick={() => setDupMatches(duplicates)}
          className="mt-3 w-full text-left rounded-xl border border-amber-200 bg-amber-50/60 px-3.5 py-2.5 text-[12.5px] text-amber-900 hover:bg-amber-50 transition-colors"
        >
          This VIN has {duplicates.length} earlier estimate
          {duplicates.length === 1 ? "" : "s"} —{" "}
          <span className="font-semibold underline underline-offset-2">review</span>
        </button>
      )}

      <VinDuplicateDialog
        open={dupMatches !== null}
        matches={dupMatches ?? []}
        onOpenExisting={() => {
          if (dupMatches?.[0]) navigate(`/estimates/${dupMatches[0].id}`)
        }}
        onContinue={() => setDupMatches(null)}
      />
    </div>
  )
}

/* ── ScanButton — the hero CTA ─────────────────── */

function ScanButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full h-[88px] px-5 rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-800 hover:from-black hover:to-zinc-900 text-white shadow-2xl shadow-zinc-900/25 hover:shadow-zinc-900/35 transition-all duration-200 flex items-center justify-between gap-4"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="size-12 rounded-xl bg-white/10 grid place-items-center backdrop-blur-sm shrink-0">
          <Camera01 className="size-6 text-white" aria-hidden />
        </div>
        <div className="text-left min-w-0">
          <p className="text-[16px] font-bold leading-tight">
            {VIN_COPY.scanLabel}
          </p>
          <p className="text-[12px] font-medium text-white/70 leading-tight mt-0.5">
            Take a photo, we read it
          </p>
        </div>
      </div>
      <ChevronRight
        className="size-5 text-white/70 shrink-0 group-hover:translate-x-1 transition-transform duration-200"
        aria-hidden
      />
    </button>
  )
}

/* ── VinLocations — three small line-art tiles ─── */
//
// "Where to find the VIN" hints inside a default-closed disclosure.
// Most users know where their VIN is; we don't waste real estate
// on every visit, but the toggle is one tap away.

function VinLocationsDisclosure() {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-zinc-50/60 transition-colors duration-150 rounded-2xl"
      >
        <span className="text-[12.5px] font-semibold text-zinc-600">
          Can&apos;t find the VIN?
        </span>
        <ChevronDown
          className={cx(
            "size-4 text-zinc-400 shrink-0 transition-transform duration-200",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-zinc-100 p-4">
              <VinLocations />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function VinLocations() {
  return (
    <div>
      <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-zinc-400 mb-3 text-center">
        Three places to find it
      </p>
      <div className="grid grid-cols-3 gap-2">
        <LocationTile label="Windshield" sub="Driver-side dash">
          <svg viewBox="0 0 80 60" className="w-full h-12" aria-hidden>
            <g stroke="#52525b" fill="none" strokeLinejoin="round" strokeLinecap="round">
              {/* Windshield rectangle */}
              <path strokeWidth="1.4" d="M 10 14 L 70 14 L 64 46 L 16 46 Z" />
              {/* Wiper hint */}
              <path strokeWidth="0.6" d="M 22 44 L 32 30" />
              <path strokeWidth="0.6" d="M 48 30 L 58 44" />
              {/* VIN plate at bottom */}
              <rect x="30" y="40" width="20" height="6" rx="0.5" strokeWidth="0.8" />
              <line x1="33" y1="43" x2="47" y2="43" strokeWidth="0.4" />
            </g>
          </svg>
        </LocationTile>
        <LocationTile label="Door jamb" sub="Sticker on the latch">
          <svg viewBox="0 0 80 60" className="w-full h-12" aria-hidden>
            <g stroke="#52525b" fill="none" strokeLinejoin="round" strokeLinecap="round">
              {/* Open door + jamb */}
              <path strokeWidth="1.4" d="M 22 6 L 22 54 L 58 54 L 58 6 Z" />
              {/* Jamb sticker */}
              <rect x="32" y="22" width="22" height="18" rx="1" strokeWidth="0.8" />
              <line x1="35" y1="27" x2="51" y2="27" strokeWidth="0.4" />
              <line x1="35" y1="31" x2="51" y2="31" strokeWidth="0.4" />
              <line x1="35" y1="35" x2="48" y2="35" strokeWidth="0.4" />
              {/* Latch hint */}
              <circle cx="58" cy="30" r="2" strokeWidth="0.6" />
            </g>
          </svg>
        </LocationTile>
        <LocationTile label="Registration" sub="On the printed card">
          <svg viewBox="0 0 80 60" className="w-full h-12" aria-hidden>
            <g stroke="#52525b" fill="none" strokeLinejoin="round" strokeLinecap="round">
              {/* Card */}
              <rect x="10" y="14" width="60" height="36" rx="2" strokeWidth="1.4" />
              <line x1="16" y1="22" x2="64" y2="22" strokeWidth="0.5" />
              <line x1="16" y1="28" x2="64" y2="28" strokeWidth="0.5" />
              <line x1="16" y1="34" x2="50" y2="34" strokeWidth="0.5" />
              <line x1="16" y1="40" x2="58" y2="40" strokeWidth="0.5" />
              {/* VIN highlight */}
              <text x="16" y="46" fontSize="4.5" fontWeight="700" fill="#10b981" stroke="none" letterSpacing="0.2">
                VIN: 1HG…
              </text>
            </g>
          </svg>
        </LocationTile>
      </div>
    </div>
  )
}

function LocationTile({
  label,
  sub,
  children,
}: {
  label: string
  sub: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl bg-white border border-zinc-200/70 p-2.5 text-center">
      {children}
      <p className="text-[11px] font-semibold text-zinc-700 leading-tight mt-1.5">
        {label}
      </p>
      <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">{sub}</p>
    </div>
  )
}

/* ── Captured thumbnail + skeleton + manual typing ─── */

function CapturedThumb({ url }: { url: string }) {
  return (
    <div className="rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50">
      <img
        src={url}
        alt="Captured VIN"
        className="w-full max-h-48 object-cover"
      />
    </div>
  )
}

function DecodeSkeleton({ label }: { label: string }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-zinc-50/40 p-5">
      <div className="flex items-center gap-3 mb-3">
        <span className="size-4 border-2 border-zinc-300 border-t-emerald-500 rounded-full animate-spin" />
        <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          {label}
        </p>
      </div>
      <div className="space-y-2 animate-pulse">
        <div className="h-5 w-3/4 rounded bg-zinc-200" />
        <div className="h-3 w-1/2 rounded bg-zinc-200/70" />
      </div>
    </div>
  )
}

function ManualVinInput({
  initialVin,
  onCancel,
  onResolved,
  onVinCandidate,
}: {
  initialVin: string
  onCancel: () => void
  onResolved: (decoded: VinDecodedVehicle) => void
  /** Reports every well-formed 17-char VIN as it's typed, before (and
   *  regardless of) decode — feeds the parent's duplicate check. */
  onVinCandidate?: (vin: string) => void
}) {
  const [vin, setVin] = useState(initialVin)
  // `dirty` flips on the first real keystroke. Without it, mounting
  // ManualVinInput with a complete `initialVin` (the Edit-VIN flow
  // from the success card) would auto-decode immediately and
  // resolve before the user could type — bouncing them straight
  // back to the success card and making Edit feel broken
  // (ClickUp 869d7dcd8).
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const cleaned = vin.replace(/[^A-HJ-NPR-Z0-9]/gi, "").toUpperCase().slice(0, 17)
  const lengthOk = cleaned.length === 17
  const formatOk = lengthOk && validateVinFormat(cleaned)
  const isEdit = initialVin.length > 0
  // A partial VIN (1–16 chars, once the user has started typing) reads as
  // an error state so it's obvious the field isn't complete. The `dirty`
  // gate keeps an Edit-VIN prefill from mounting red (ClickUp 869d7dcd8).
  const isShort = dirty && cleaned.length > 0 && cleaned.length < 17

  useEffect(() => {
    if (formatOk && dirty) onVinCandidate?.(cleaned)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleaned, formatOk, dirty])

  useEffect(() => {
    if (!formatOk || !dirty) return
    let cancelled = false
    setSubmitting(true)
    setError(null)
    decodeVIN(cleaned)
      .then((decoded) => {
        if (cancelled) return
        // Hard-gate: only accept VINs the BE actually recognized.
        // Empty make/model = BE could not validate this number;
        // refuse it here rather than letting garbage VINs flow
        // through with empty vehicle fields (ClickUp 869d7dcr8).
        if (!decoded.make || !decoded.model) {
          setError("VIN not recognized — please double-check the number.")
          setSubmitting(false)
          return
        }
        onResolved({
          vin: cleaned,
          year: decoded.year,
          make: decoded.make,
          model: decoded.model,
          trim: decoded.trim,
          type: decoded.type,
        })
        setSubmitting(false)
      })
      .catch(() => {
        if (cancelled) return
        setError("VIN not recognized — please double-check the number.")
        setSubmitting(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleaned, dirty])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setVin(raw)
    setDirty(true)
    // Surface forbidden chars rather than silently swallowing them.
    // The displayed `cleaned` strips anything outside [A-HJ-NPR-Z0-9],
    // so without this the keystroke just disappears and the user has
    // no idea why (ClickUp 869d7dcr8).
    const stripped = raw
      .toUpperCase()
      .replace(/[A-HJ-NPR-Z0-9]/g, "")
      .replace(/\s+/g, "")
    if (stripped) {
      const unique = Array.from(new Set(stripped.split(""))).join(", ")
      setError(`${unique} can't be in a VIN — I, O and Q are excluded to avoid confusion.`)
    } else if (error) {
      setError(null)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className="space-y-5"
    >
      <Field label="VIN" required hint={VIN_COPY.inputHelp} error={error ?? undefined}>
        <input
          autoFocus
          value={cleaned}
          onChange={handleChange}
          placeholder={VIN_COPY.inputPlaceholder}
          maxLength={17}
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          className={cx(
            onboardingInputClass,
            "font-mono tracking-[0.06em] uppercase text-[16px]",
            (error || isShort) && onboardingInputErrorClass,
          )}
        />
      </Field>

      <div className="flex items-center justify-between">
        {submitting ? (
          <p className="inline-flex items-center gap-2 text-[12.5px] text-zinc-500">
            <span className="size-3 border-2 border-zinc-300 border-t-emerald-500 rounded-full animate-spin" />
            {VIN_COPY.reading}
          </p>
        ) : (
          <p
            className={cx(
              "text-[12px] tabular-nums",
              lengthOk
                ? "text-emerald-600"
                : isShort
                  ? "text-red-500"
                  : "text-zinc-400",
            )}
          >
            {cleaned.length} / 17
          </p>
        )}
      </div>

      <p className="text-[11.5px] text-zinc-400 leading-snug">
        {VIN_COPY.plateTip}
      </p>

      <button
        type="button"
        onClick={onCancel}
        className="text-[13px] font-semibold text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors duration-150"
      >
        {isEdit ? "← Keep current VIN" : "← Back to scanning"}
      </button>
    </motion.div>
  )
}
